import React, { useState } from 'react';
import { faqs } from '../data/siteContent';

// Generated from the same array the page renders, so the structured data can
// never drift from the visible answers.
const faqJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

const slug = (question: string) =>
  question
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const FaqSection: React.FC = () => {
  // Multiple answers may stay open — closing one to read another is a needless
  // rule on a page people scan.
  const [openSet, setOpenSet] = useState<Set<number>>(() => new Set([0]));

  const toggle = (index: number) =>
    setOpenSet((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  return (
    <section id="faq" className="section-y">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
      <div className="shell">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-gutter">
          <header className="lg:col-span-4">
            <p className="kicker-action">FAQ</p>
            <h2 className="display-lg mt-5 text-[2.25rem] sm:text-[2.85rem]">
              Answers, including the awkward ones.
            </h2>
          </header>

          <dl className="lg:col-span-8">
            {faqs.map((item, index) => {
              const isOpen = openSet.has(index);
              const id = slug(item.question);
              return (
                <div key={item.question} className="border-t border-ink/12 last:border-b last:border-ink/12">
                  <dt>
                    <button
                      type="button"
                      id={`faq-${id}`}
                      className="flex w-full items-start justify-between gap-6 py-6 text-left"
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${id}`}
                      onClick={() => toggle(index)}
                    >
                      <span className="display-sm text-lg sm:text-xl">{item.question}</span>
                      <span
                        className="mt-0.5 shrink-0 font-display text-2xl leading-none text-primary"
                        aria-hidden="true"
                      >
                        {isOpen ? '–' : '+'}
                      </span>
                    </button>
                  </dt>
                  <dd
                    id={`faq-panel-${id}`}
                    role="region"
                    aria-labelledby={`faq-${id}`}
                    hidden={!isOpen}
                    className="max-w-reading pb-7 pr-10"
                  >
                    <p className="prose-note">{item.answer}</p>
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
